import React, { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import Layout from '../../../components/Layout.jsx';

const PIPELINE = [
  { key: 'Draft', label: 'Draft' },
  { key: 'In Review', label: 'In Review' },
  { key: 'Approved', label: 'Approved' },
  { key: 'State Submitted', label: 'State Submitted' },
  { key: 'Launched', label: 'Launched' },
];

const STATUS_BADGE_PALETTE = {
  'Draft':           'bg-amber-50 text-amber-700 border-amber-200',
  'In Review':       'bg-blue-50 text-blue-700 border-blue-200',
  'Approved':        'bg-violet-50 text-violet-700 border-violet-200',
  'State Submitted': 'bg-cyan-50 text-cyan-700 border-cyan-200',
  'Launched':        'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Learnworlds':     'bg-gray-100 text-gray-600 border-gray-200',
  'Archived':        'bg-gray-100 text-gray-600 border-gray-200',
};

function StatusBadge({ status }) {
  if (!status) return null;
  const palette = STATUS_BADGE_PALETTE[status] || 'bg-gray-100 text-gray-700 border-gray-200';
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${palette}`}>
      {status}
    </span>
  );
}

function ProgressTracker({ courseId, status }) {
  const currentIndex = PIPELINE.findIndex((s) => s.key === status);
  const offTrack = currentIndex === -1 && status;
  const [pending, setPending] = useState(null);

  const setStatus = (newStatus) => {
    if (newStatus === status || pending) return;
    setPending(newStatus);
    router.patch(
      `/superadmin/admin_courses/${courseId}/status`,
      { admin_course: { status: newStatus } },
      {
        preserveScroll: true,
        onFinish: () => setPending(null),
      }
    );
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      <div className="flex items-baseline justify-between mb-5">
        <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Pipeline</h2>
      </div>

      <ol className="flex items-start">
        {PIPELINE.map((step, idx) => {
          const isComplete  = currentIndex > idx;
          const isCurrent   = currentIndex === idx;
          const isPending   = pending === step.key;
          const isAnyPending = pending != null;

          const circleClass = isComplete
            ? 'bg-indigo-600 border-indigo-600 text-white group-hover:bg-indigo-700 group-hover:border-indigo-700'
            : isCurrent
              ? 'bg-white border-indigo-600 text-indigo-600 ring-4 ring-indigo-100'
              : 'bg-white border-gray-200 text-gray-400 group-hover:border-indigo-300 group-hover:text-indigo-500';

          const labelClass = isCurrent
            ? 'text-gray-900 font-semibold'
            : isComplete
              ? 'text-gray-700 font-medium group-hover:text-indigo-700'
              : 'text-gray-400 group-hover:text-indigo-600';

          return (
            <li key={step.key} className="flex-1 flex flex-col items-center relative">
              {idx > 0 && (
                <div
                  className={`absolute top-4 -left-1/2 right-1/2 h-0.5 ${
                    currentIndex > idx - 1 ? 'bg-indigo-600' : 'bg-gray-200'
                  }`}
                  aria-hidden="true"
                />
              )}
              <button
                type="button"
                onClick={() => setStatus(step.key)}
                disabled={isAnyPending || isCurrent}
                title={isCurrent ? 'Current status' : `Set status to ${step.label}`}
                className={`group relative z-10 flex flex-col items-center bg-transparent cursor-pointer disabled:cursor-default ${isAnyPending && !isPending ? 'opacity-50' : ''}`}
              >
                <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-xs font-bold transition ${circleClass}`}>
                  {isPending ? (
                    <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  ) : isComplete ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    idx + 1
                  )}
                </div>
                <div className={`mt-2 text-xs text-center px-1 transition ${labelClass}`}>{step.label}</div>
              </button>
            </li>
          );
        })}
      </ol>

      <p className="mt-4 text-xs text-gray-400 text-center">Click any stage to update the course status.</p>
    </div>
  );
}

function DetailCard({ title, icon, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        {icon && (
          <div className="w-8 h-8 bg-indigo-50 text-indigo-600 rounded-lg flex items-center justify-center">
            {icon}
          </div>
        )}
        <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function MetaRow({ label, value }) {
  if (value == null || value === '') return null;
  return (
    <div className="flex items-baseline justify-between gap-4 py-2 border-b border-gray-100 last:border-b-0 last:pb-0 first:pt-0">
      <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</dt>
      <dd className="text-sm text-gray-900 text-right">{value}</dd>
    </div>
  );
}

function GenerationModal({ open, title, description, ctaLabel, submitting, onClose, onSubmit }) {
  const [text, setText] = useState('');

  if (!open) return null;

  const submit = () => {
    if (submitting) return;
    onSubmit(text);
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          <p className="text-xs text-gray-500 mt-1">{description}</p>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Additional context <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={5}
            placeholder="Anything the AI should know — focus areas, audience tweaks, tone, references to emphasize, citations to include, etc."
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            autoFocus
          />
          <p className="text-[11px] text-gray-400 mt-1">Leave blank to use only the course record fields.</p>
        </div>

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg text-gray-700 bg-gray-100 hover:bg-gray-200 transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Starting…' : ctaLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function StepRow({ title, description, status, busyStatus, doneStatus, failStatus, busyMessage, disabled, disabledMessage, ctaLabel, ctaDoneLabel, onRun }) {
  const [submitting, setSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const isBusy   = status === busyStatus;
  const isDone   = status === doneStatus;
  const isFailed = status === failStatus;
  const cta = isDone ? ctaDoneLabel : ctaLabel;

  const submit = (additionalContext) => {
    if (submitting || isBusy || disabled) return;
    setSubmitting(true);
    onRun({
      additionalContext,
      onFinish: () => {
        setSubmitting(false);
        setModalOpen(false);
      },
    });
  };

  return (
    <div className="border border-gray-200 rounded-xl p-4 space-y-2">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {isDone && <span className="text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">Ready</span>}
        {isFailed && <span className="text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">Failed</span>}
      </div>
      <p className="text-xs text-gray-600">{description}</p>

      {isBusy && (
        <div className="flex items-center gap-2 text-xs text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-lg px-2.5 py-1.5">
          <svg className="w-3.5 h-3.5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          {busyMessage}
          <button type="button" onClick={() => router.reload({ preserveScroll: true })} className="ml-auto text-indigo-700 underline font-semibold">
            Refresh
          </button>
        </div>
      )}

      {disabled && !isBusy && (
        <p className="text-xs text-gray-500 italic">{disabledMessage}</p>
      )}

      <button
        type="button"
        onClick={() => {
          if (submitting || isBusy || disabled) return;
          setModalOpen(true);
        }}
        disabled={submitting || isBusy || disabled}
        className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {cta}
      </button>

      <GenerationModal
        open={modalOpen}
        title={cta}
        description={description}
        ctaLabel={cta}
        submitting={submitting}
        onClose={() => { if (!submitting) setModalOpen(false); }}
        onSubmit={submit}
      />
    </div>
  );
}

const EBOOK_READY_STATUSES = ['ebook_ready', 'quizzes_generating', 'quizzes_ready', 'quizzes_failed'];

function CourseCreatorCard({ course }) {
  const post = (path, { additionalContext = '', onFinish } = {}) =>
    router.post(
      `/superadmin/admin_courses/${course.id}/${path}`,
      { additional_context: additionalContext },
      { preserveScroll: true, onFinish }
    );
  const hasOutline = !!course.course_toc;
  const ebookReady = EBOOK_READY_STATUSES.includes(course.status);

  return (
    <DetailCard
      title="Course Creator"
      icon={
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-gray-600">
          AI-generated course content. Each step uses the title, role, hours, license types, description, and
          notes on this record. Outputs are written to the SharePoint folder for this course.
        </p>

        <StepRow
          title="1 · Outline"
          description="Generate the course outline (modules + learning objectives). Saved here as Table of Contents and to SharePoint as outline.md / outline.docx."
          status={course.status}
          busyStatus="outline_generating"
          doneStatus="outline_ready"
          failStatus="outline_failed"
          busyMessage="Outline is generating. Usually takes 30–90 seconds."
          ctaLabel="Generate outline"
          ctaDoneLabel="Regenerate outline"
          onRun={(opts) => post('generate_outline', opts)}
        />

        <StepRow
          title="2 · Module ebooks"
          description="For each module: ebook outline → per-chapter markdown → merged module markdown + DOCX (admin courses include a glossary). Uploaded under _RAG/ and EBOOK/."
          status={course.status}
          busyStatus="ebook_generating"
          doneStatus="ebook_ready"
          failStatus="ebook_failed"
          busyMessage="Generating module ebooks. This usually takes several minutes."
          disabled={!hasOutline}
          disabledMessage="Generate the outline first."
          ctaLabel="Generate ebooks"
          ctaDoneLabel="Regenerate ebooks"
          onRun={(opts) => post('generate_ebook', opts)}
        />

        <StepRow
          title="3 · Module quizzes"
          description="For each module: AI-generated quiz uploaded to SharePoint at QUIZ/module_N_quiz.xlsx."
          status={course.status}
          busyStatus="quizzes_generating"
          doneStatus="quizzes_ready"
          failStatus="quizzes_failed"
          busyMessage="Generating module quizzes."
          disabled={!ebookReady}
          disabledMessage="Generate the module ebooks first."
          ctaLabel="Generate quizzes"
          ctaDoneLabel="Regenerate quizzes"
          onRun={(opts) => post('generate_quizzes', opts)}
        />
      </div>
    </DetailCard>
  );
}

export default function SuperadminAdminCoursesShow({ course = {} }) {
  const facilityTypes = course.license_types || [];
  const sources = course.sources || [];
  const assignment = course.review_assignment;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 sm:p-8 space-y-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Link href="/superadmin/admin_courses" className="hover:text-indigo-600 transition">
            ← All courses
          </Link>
        </div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-2 rounded-full bg-purple-50 text-purple-700 border border-purple-200 text-xs font-medium">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Superadmin · Course
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">{course.name}</h1>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <StatusBadge status={course.status} />
              {course.role && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
                  {course.role}
                </span>
              )}
              {course.course_type && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                  {course.course_type}
                </span>
              )}
              {course.ceu_credit_hours != null && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                  {course.ceu_credit_hours} CEU hr
                </span>
              )}
            </div>
          </div>
          <Link
            href={`/superadmin/admin_courses/${course.id}/edit`}
            className="shrink-0 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edit
          </Link>
        </div>

        {/* Pipeline progress */}
        <ProgressTracker courseId={course.id} status={course.status} />

        {/* Main grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column */}
          <div className="lg:col-span-2 space-y-6">
            {course.description && (
              <DetailCard
                title="Description"
                icon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6M5 4h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z" />
                  </svg>
                }
              >
                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{course.description}</p>
              </DetailCard>
            )}

            {course.learning_objectives && (
              <DetailCard
                title="Learning Objectives"
                icon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                }
              >
                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{course.learning_objectives}</p>
              </DetailCard>
            )}

            <CourseCreatorCard course={course} />

            {course.course_toc && (
              <DetailCard
                title="Table of Contents"
                icon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                }
              >
                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{course.course_toc}</p>
              </DetailCard>
            )}

            <DetailCard
              title={`Sources (${sources.length})`}
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              }
            >
              {sources.length === 0 ? (
                <p className="text-sm text-gray-500">No sources linked.</p>
              ) : (
                <div className="divide-y divide-gray-100">
                  {sources.map((s) => (
                    <div key={s.id} className="py-3 first:pt-0 last:pb-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-gray-900">{s.title}</p>
                          {s.author_organization && (
                            <p className="text-xs text-gray-500 mt-0.5">{s.author_organization}</p>
                          )}
                          <div className="mt-1 flex flex-wrap gap-1.5">
                            {s.source_type && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-700">{s.source_type}</span>
                            )}
                            {s.priority_level && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200">{s.priority_level}</span>
                            )}
                            {s.format && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-200">{s.format}</span>
                            )}
                          </div>
                        </div>
                        {s.url_or_document_reference && /^https?:\/\//.test(s.url_or_document_reference) && (
                          <a
                            href={s.url_or_document_reference}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="shrink-0 text-xs font-semibold text-indigo-600 hover:text-indigo-700"
                          >
                            Open ↗
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </DetailCard>

            {course.additional_notes && (
              <DetailCard
                title="Additional Notes"
                icon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-4 4z" />
                  </svg>
                }
              >
                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{course.additional_notes}</p>
              </DetailCard>
            )}
          </div>

          {/* Right column */}
          <div className="space-y-6">
            <DetailCard
              title="At a Glance"
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
            >
              <dl>
                <MetaRow label="Role" value={course.role} />
                <MetaRow label="Course type" value={course.course_type} />
                <MetaRow label="CEU hours" value={course.ceu_credit_hours} />
                <MetaRow label="Fee" value={course.course_fee != null ? `$${course.course_fee}` : null} />
                <MetaRow
                  label="Facility types"
                  value={
                    facilityTypes.length > 0 ? (
                      <div className="flex flex-wrap gap-1 justify-end">
                        {facilityTypes.map((ft) => (
                          <span key={ft} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold font-mono bg-blue-50 text-blue-700">{ft}</span>
                        ))}
                      </div>
                    ) : null
                  }
                />
                <MetaRow
                  label="Core of knowledge"
                  value={
                    (course.core_of_knowledge || []).length > 0 ? (
                      <div className="flex flex-wrap gap-1 justify-end">
                        {course.core_of_knowledge.map((c) => (
                          <span key={c} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-50 text-purple-700 border border-purple-200">{c}</span>
                        ))}
                      </div>
                    ) : null
                  }
                />
                <MetaRow
                  label="SharePoint"
                  value={
                    course.sharepoint_link ? (
                      <a href={course.sharepoint_link} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-700 font-semibold">
                        Open ↗
                      </a>
                    ) : null
                  }
                />
              </dl>
            </DetailCard>

            <DetailCard
              title="Clinical Reviewer"
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              }
            >
              {course.reviewer ? (
                <div>
                  <Link href={`/superadmin/reviewers/${course.reviewer.id}`} className="block group">
                    <p className="text-sm font-semibold text-gray-900 group-hover:text-indigo-600 transition">{course.reviewer.name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{course.reviewer.email}</p>
                  </Link>
                  {assignment && (
                    <div className="mt-4 pt-4 border-t border-gray-100 space-y-1.5 text-xs text-gray-600">
                      {assignment.status && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Status</span>
                          <span className="font-medium text-gray-900 capitalize">{assignment.status}</span>
                        </div>
                      )}
                      {assignment.assigned_at && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Assigned</span>
                          <span className="text-gray-900">{assignment.assigned_at}</span>
                        </div>
                      )}
                      {assignment.accessed_at && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">First viewed</span>
                          <span className="text-gray-900">{assignment.accessed_at}</span>
                        </div>
                      )}
                      {assignment.submitted_at && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Submitted</span>
                          <span className="text-gray-900">{assignment.submitted_at}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No reviewer assigned.</p>
              )}
            </DetailCard>

            <DetailCard title="Record">
              <dl>
                <MetaRow label="Created" value={course.created_at} />
                <MetaRow label="Updated" value={course.updated_at} />
              </dl>
            </DetailCard>
          </div>
        </div>
      </div>
    </Layout>
  );
}
